import React, { useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { abortCampaign } from "../apis/campaign";
import { toast } from "react-toastify";

/**
 * Abort Campaign Modal
 * Allows user to abort an active or scheduled campaign
 */
const AbortCampaignModal = ({ isOpen, onClose, campaign, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleAbort = async () => {
    try {
      setLoading(true);
      const response = await abortCampaign(campaign.campaign_id);

      if (response.data.success) {
        toast.success(response.data.message || "Campaign aborted successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response.data.message || "Failed to abort campaign");
      }
    } catch (error) {
      console.error("Error aborting campaign:", error);
      toast.error(error.response?.data?.error || "Failed to abort campaign");
    } finally {
      setLoading(false);
    }
  };

  const isScheduled = campaign.status === "scheduled";
  const isActive = campaign.status === "active";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">Abort Campaign</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-gray-700 mb-4">
            Are you sure you want to abort this campaign?
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-xs font-medium text-yellow-900 mb-1">Campaign Details:</p>
            <p className="text-xs text-yellow-800">
              <strong>Name:</strong> {campaign.campaign_name}
            </p>
            <p className="text-xs text-yellow-800 mt-1">
              <strong>Status:</strong> {campaign.status}
            </p>
            {isScheduled && campaign.scheduled_time_original && (
              <p className="text-xs text-yellow-800 mt-1">
                <strong>Scheduled:</strong> {campaign.scheduled_time_original} ({campaign.timezone})
              </p>
            )}
          </div>

          {isActive && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-900">
                <strong>Note:</strong> This campaign is currently sending. It will stop after the
                current batch of messages is processed.
              </p>
            </div>
          )}

          {isScheduled && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-900">
                <strong>Note:</strong> This scheduled campaign will be cancelled and will not be sent.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAbort}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Aborting...
                </>
              ) : (
                "Abort Campaign"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbortCampaignModal;

